import axios from "axios";
import { useAuthStore } from "../store/authStore";

// Use environment variable for API base URL
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://los_galaxy.test";
// const API_BASE_URL = "https://los_galaxy.test";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true, // Important for session-based auth with cookies
});

// Request interceptor to add auth token (if using token-based auth as fallback)
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    const selectedProductId = useAuthStore.getState().selectedProductId;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Always let the browser set multipart/form-data (with boundary) for FormData payloads.
    // Doing this unconditionally — not only when a product id is present — prevents
    // uploads from being sent with the default application/json header, which silently
    // corrupts the request body on the server.
    const isFormData = config.data instanceof FormData;
    if (isFormData) {
      delete config.headers['Content-Type'];
    }

    // Add product_id to all requests if available
    if (selectedProductId) {
      // Add as header
      config.headers['X-Product-Id'] = selectedProductId;

      // Also add to query params for GET requests
      if (config.method === 'get') {
        config.params = {
          ...config.params,
          product_id: selectedProductId
        };
      }
      // Add to body for POST/PUT/PATCH requests
      else if (['post', 'put', 'patch'].includes(config.method)) {
        if (isFormData) {
          // Avoid appending duplicate product_id on retry (same FormData reused)
          if (!config.data.has('product_id')) {
            config.data.append('product_id', selectedProductId);
          }
        }
        // Handle JSON data
        else if (config.headers['Content-Type'] === 'application/json') {
          const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
          config.data = {
            ...body,
            product_id: selectedProductId
          };
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth state
      useAuthStore.getState().logout();
      
      // Don't use window.location.href as it causes hard reload
      // Let React Router handle navigation to avoid infinite loops
      // The ProtectedRoute component will redirect to login
    }
    return Promise.reject(error);
  }
);

export default api;
